import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';
import { getBookingsStatuses } from 'store/v1/statuses/statuses.selectors.js';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';

import classnames from 'classnames';
import Color from 'color';
import moment from 'moment-timezone';
import { rd } from '@passionware/monads';

import FullCalendarEvent from 'v1/components/feature/Calendars/CalendarEventComponents/FullCalendarEvent/FullCalendarEvent';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { TailwindRoot } from 'v4/shared/TailwindRoot';
import { Button } from 'v5/design-system/Button';
import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxQueryRenderer,
  ComboboxTrigger
} from 'v5/design-system/Combobox';
import { ChevronDown, PencilLine } from 'lucide-react';

import './BigCalendar.scss';
import 'styles/v1/FullCalendar.scss';
import { cn } from 'v5/platform/dom/cn';

const getStatusBadgeStyle = hex => {
  if (!hex) return undefined;
  try {
    const base = Color(hex);
    const baseLuminosity = base.luminosity();
    const isLightBase = baseLuminosity > 0.5;

    const textColor = isLightBase
      ? base.mix(Color('black'), 0.75)
      : base.mix(Color('white'), 0.75);

    const style = {
      backgroundColor: base.hex(),
      color: textColor.hex()
    };

    const isNearWhite = baseLuminosity > 0.85;
    if (isNearWhite) {
      style.border = `1px solid ${base.mix(Color('black'), 0.05).hex()}`;
    } else {
      style.border = `1px solid transparent`;
    }

    return style;
  } catch (error) {
    return undefined;
  }
};

const CALENDAR_TZ = moment.tz.guess();

export const convertDateToDateTime = (date, isEndOfDay, timezone) => {
  const tz = timezone || moment().tz();
  const dateM = moment.tz(date, tz);

  if (isEndOfDay) {
    dateM.subtract(1, 'days');
  }
  return dateM.format('YYYY-MM-DD');
};

export const BigCalendar = props => {
  const bookingStatuses = useSelector(getBookingsStatuses);
  const users = useSelector(state => state.accounts.data);

  const [activeStatus, setActiveStatus] = useState();
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);
  const { events, resourceName } = props;
  const calendarRef = useRef(null);
  const containerRef = useRef(null);
  useEffect(() => {
    if (!bookingStatuses || bookingStatuses.length === 0) {
      if (activeStatus) {
        setActiveStatus(null);
        setIsDrawingEnabled(false);
      }
      return;
    }

    if (
      activeStatus &&
      bookingStatuses.some(status => status.id === activeStatus.id)
    ) {
      return;
    }

    setActiveStatus(bookingStatuses[0] || null);
  }, [bookingStatuses, activeStatus]);

  // Handle resize events (e.g., when MainSidebar is shown/hidden)
  useEffect(() => {
    if (!containerRef.current || !calendarRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to sync with browser's render cycle
      requestAnimationFrame(() => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
          calendarApi.updateSize();
        }
      });
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  function getUserName(userId) {
    if (userId && users) {
      const user = users[userId];
      if (user) {
        return `by ${user.displayName}`;
      }
    }
    return resourceName ? `by ${resourceName}` : ''; // if lastUpdateUserId is null, use we can assume it was updated by the resource in the availability portal
  }

  const renderEventContent = arg => {
    const eventData = {
      ...arg.event.extendedProps,
      title: arg.event.title,
      borderColor: arg.borderColor
    };

    const tooltipTitle = `Updated ${getUserName(
      eventData.lastUpdateUserId
    )} ${moment(eventData.updated_at).fromNow()}`;

    return (
      <SimpleTooltip title={tooltipTitle} side="bottom" delayDuration={300}>
        <FullCalendarEvent event={eventData} />
      </SimpleTooltip>
    );
  };

  const activeStatusName = activeStatus ? activeStatus.name : 'Select status';
  const activeStatusColour = activeStatus?.colour;
  const statusBadgeStyle = activeStatus
    ? getStatusBadgeStyle(activeStatusColour)
    : { border: '1px solid transparent' };
  const drawingModeActive = Boolean(isDrawingEnabled && activeStatus);

  const instructions = drawingModeActive
    ? 'Drag across the calendar to apply the selected status.'
    : 'Choose a status, then drag across the calendar to apply it.';

  const drawingModeLabel = drawingModeActive
    ? 'Drawing mode • On'
    : 'Drawing mode • Off';

  const drawingModeBadgeClass = cn(
    'inline-flex items-center gap-2 rounded-full px-3 py-0'
  );

  const drawingModeDotClass = cn(
    'size-2.5 rounded-full',
    drawingModeActive ? 'bg-brand-500' : 'bg-fg-subtle'
  );

  const filteredStatusesRenderer = useMemo(
    () =>
      (query = '') => {
        const search = query.trim().toLowerCase();
        const filtered = (bookingStatuses || []).filter(status =>
          status.name.toLowerCase().includes(search)
        );

        return rd.of(
          <ComboboxGroup>
            {filtered.map(status => (
              <ComboboxItem
                key={status.id}
                value={String(status.id)}
                isSelected={activeStatus?.id === status.id}
                className="flex items-center gap-2"
                onSelect={() => {
                  setActiveStatus(status);
                  setIsDrawingEnabled(true);
                }}
              >
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: status.colour }}
                />
                <span className="flex-1 truncate text-sm">{status.name}</span>
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        );
      },
    [bookingStatuses, activeStatus]
  );

  return (
    <>
      <div
        ref={containerRef}
        className={classnames([
          'FullCalendar FullCalendar--big',
          { drawable: Boolean(isDrawingEnabled && activeStatus) }
        ])}
      >
        <FullCalendar
          ref={calendarRef}
          selectable={Boolean(isDrawingEnabled && activeStatus)}
          editable
          initialView="dayGridMonth"
          fixedWeekCount={false}
          dayMaxEventRows
          timeZone={CALENDAR_TZ}
          longPressDelay={200}
          handleWindowResize={false}
          plugins={[dayGridPlugin, interactionPlugin, momentTimezonePlugin]}
          events={events}
          eventDidMount={({ el }) => {
            el.removeAttribute('tabindex');
          }}
          eventClassNames={['BigCalendarEventReset']}
          eventContent={renderEventContent}
          eventClick={({ event = {} }) => {
            props.onSelectEvent && props.onSelectEvent(event.extendedProps);
          }}
          datesSet={({ view = {} }) => {
            props.onRangeChange({
              start: view.activeStart,
              end: view.activeEnd
            });
          }}
          buttonText={{
            today: 'Today',
            month: 'Month',
            week: 'Week',
            day: 'Day',
            list: 'List'
          }}
          eventResize={({ event = {} }) => {
            props.onEventDrop({
              event: {
                id: parseFloat(event.id),
                ...event.extendedProps
              },
              start: convertDateToDateTime(event.start, false, CALENDAR_TZ),
              end: convertDateToDateTime(event.end, true, CALENDAR_TZ)
            });
          }}
          eventDrop={({ event = {} }) => {
            props.onEventDrop({
              event: {
                id: parseFloat(event.id),
                ...event.extendedProps
              },
              start: convertDateToDateTime(event.start, false, CALENDAR_TZ),
              end: convertDateToDateTime(event.end, true, CALENDAR_TZ)
            });
          }}
          select={({ startStr, endStr }) => {
            if (!isDrawingEnabled || !activeStatus) return;
            props.onSelectSlot(activeStatus, {
              start: startStr,
              end: moment(endStr).subtract(1, 'days').format('YYYY-MM-DD')
            });
          }}
        />
      </div>
      <TailwindRoot>
        <div className="rounded-b-lg border-t-0 border border-fg-subtle bg-bg-default p-2 pl-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="flex items-center gap-2 text-sm font-semibold text-fg-muted">
              <PencilLine className="h-4 w-4" aria-hidden />
              {instructions}
            </span>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant={drawingModeActive ? 'tertiarySelected' : 'tertiary'}
                size="xs"
                aria-pressed={drawingModeActive}
                disabled={!activeStatus}
                onClick={() => {
                  if (!activeStatus) return;
                  setIsDrawingEnabled(prev => !prev);
                }}
                className={drawingModeBadgeClass}
              >
                <span className={drawingModeDotClass} aria-hidden />
                {drawingModeLabel}
              </Button>
              <Combobox>
                <ComboboxTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className={classnames(
                      'gap-2 py-1 pl-1',
                      !activeStatus && 'text-fg-muted'
                    )}
                  >
                    <span
                      className={classnames(
                        'inline-flex items-center rounded-sm p-1 py-0.5 text-sm font-semibold',
                        activeStatus ? '' : 'bg-bg-subtle text-fg-muted'
                      )}
                      style={statusBadgeStyle}
                    >
                      {activeStatusName}
                    </span>
                    <ChevronDown className="size-4 text-fg-muted" />
                  </Button>
                </ComboboxTrigger>
                <ComboboxContent
                  align="end"
                  className="max-h-72 w-64 overflow-hidden rounded-md border border-border-default bg-bg-default p-0"
                >
                  <ComboboxInput
                    placeholder="Search statuses…"
                    className="border-b border-border-default px-3 py-2 text-sm focus:outline-none"
                  />
                  <ComboboxList>
                    <ComboboxQueryRenderer emptyContent="No statuses found">
                      {(query, isOpen) =>
                        isOpen ? filteredStatusesRenderer(query) : rd.of(<></>)
                      }
                    </ComboboxQueryRenderer>
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </div>
          </div>
        </div>
      </TailwindRoot>
    </>
  );
};

BigCalendar.propTypes = {
  events: PropTypes.array,
  timezone: PropTypes.string,
  onSelectSlot: PropTypes.func,
  onRangeChange: PropTypes.func,
  onSelectEvent: PropTypes.func
};

export default BigCalendar;
